/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Ruby SDK", description = "Hightouch's Ruby SDK for collecting events", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Ruby SDK",
        description: "Hightouch's Ruby SDK for collecting events",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Ruby SDK makes it easy to track events from Ruby applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Ruby SDK can be installed via\n", _jsx(_components.a, {
          href: "https://rubygems.org/gems/events-sdk-ruby/",
          children: "RubyGems.org"
        }), ". You may add the SDK\nto your Gemfile if you're using Bundler, or install it directly:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "gem install events-sdk-ruby\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "To initialize the Ruby SDK in your application, create a new Analytics instance:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "require"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hightouch'"
          }), "\n\nanalytics = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-variable constant_",
            children: "Analytics"
          }), ".", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), "(\n  write_key: WRITE_KEY,\n  host: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'us-east-1.hightouch-events.com'"
          }), "\n)\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "identify"
          }), "(\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "user_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  }\n)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "track"
          }), "(\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "user_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "event"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Order completed\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "total"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  }\n)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "page"
        }), " method sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-less",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".page"
          }), "(\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "user_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-less",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".screen"
          }), "(\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "user_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Hash"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "group"
          }), "(\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "user_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "group_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "company_location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  },\n)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The Ruby SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod. ", _jsx(_components.code, {
          children: "flush"
        }), " should be called when shutting down your Ruby app to\nmake sure no events are lost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-lua",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "flush"
          }), "()\n"]
        })
      })]
    });
  }
}
export default MDXContent;
